import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronDownIcon, GlobeIcon } from './icons';

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'ar', label: 'العربية', short: 'AR' },
] as const;

// Globe + EN/AR + Chevron Button that opens a Menu of language options. Same shape
// as the landing-page header's language switcher, lifted out so any layout
// (Landing, Auth, future Dashboard) can drop it in without duplicating the menu wiring.
export function LanguageMenuButton() {
  const { t, i18n } = useTranslation();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const currentLang =
    LANGUAGE_OPTIONS.find((opt) => opt.code === i18n.language) ?? LANGUAGE_OPTIONS[0];

  const openMenu = (event: MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget);
  const closeMenu = () => setAnchor(null);
  const pickLanguage = (code: 'ar' | 'en') => {
    void i18n.changeLanguage(code);
    closeMenu();
  };

  return (
    <>
      <Button
        size="small"
        onClick={openMenu}
        startIcon={<GlobeIcon />}
        endIcon={<ChevronDownIcon />}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        aria-label={t('header.languageLabel', { defaultValue: 'Change language' })}
        sx={{
          color: 'text.primary',
          fontSize: 14,
          fontWeight: 500,
          px: 1.25,
        }}
      >
        {currentLang.short}
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={closeMenu}
        slotProps={{
          paper: { sx: { mt: 1, borderRadius: (t) => `${t.shape.borderRadius}px`, minWidth: 140 } },
        }}
      >
        {LANGUAGE_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.code}
            onClick={() => pickLanguage(opt.code)}
            selected={opt.code === i18n.language}
            sx={{ fontSize: 14 }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
